class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.scrollThreshold = this.dataset.scrollThreshold
      ? Number(this.dataset.scrollThreshold)
      : 120;

    this.forceScrolled =
      this.dataset.forceScrolled === 'true';

    this.cartCounts = this.querySelectorAll(
      '[data-header-cart-count]'
    );

    this.cartTriggers = this.querySelectorAll(
      '[data-rebuy-cart-trigger]'
    );

    this.navItems = this.querySelectorAll(
      '.site-header__nav-item'
    );

    this.megaMenuItems = this.querySelectorAll(
      '.site-header__nav-item--mega'
    );

    this.megaMenuLinks = this.querySelectorAll(
      '.site-header__mega-menu-link'
    );

    this.mobileMenuButton = this.querySelector(
      '[data-mobile-menu-button]'
    );

    this.mobileMenuClose = this.querySelector(
      '[data-mobile-menu-close]'
    );

    this.mobileMenu = this.querySelector(
      '[data-mobile-menu]'
    );

    this.mobileCarousels = this.querySelectorAll(
      '[data-mobile-carousel]'
    );

    this.activeMegaMenuItem = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);

    this.handleCartTriggerClick =
      this.handleCartTriggerClick.bind(this);

    this.handleMegaMenuMouseEnter =
      this.handleMegaMenuMouseEnter.bind(this);

    this.handleMegaMenuFocusIn =
      this.handleMegaMenuFocusIn.bind(this);

    this.handleMegaMenuLinkMouseEnter =
      this.handleMegaMenuLinkMouseEnter.bind(this);

    this.handleMegaMenuLinkFocus =
      this.handleMegaMenuLinkFocus.bind(this);

    this.handleNavItemMouseEnter =
      this.handleNavItemMouseEnter.bind(this);

    this.handleDocumentPointerMove =
      this.handleDocumentPointerMove.bind(this);

    this.handleEscapeKey =
      this.handleEscapeKey.bind(this);

    this.handleMobileMenuButtonClick =
      this.handleMobileMenuButtonClick.bind(this);

    this.handleMobileMenuCloseClick =
      this.handleMobileMenuCloseClick.bind(this);

    this.handleMobileMenuOverlayClick =
      this.handleMobileMenuOverlayClick.bind(this);

    this.handleMobileCarouselScroll =
      this.handleMobileCarouselScroll.bind(this);

    this.handleResize =
      this.handleResize.bind(this);
  }

  connectedCallback() {
    this.handleScroll();
    this.refreshCartCount();

    window.addEventListener(
      'scroll',
      this.handleScroll,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      this.handleResize
    );

    document.addEventListener(
      'cart:update',
      this.handleCartUpdate
    );

    document.addEventListener(
      'keydown',
      this.handleEscapeKey
    );

    document.addEventListener(
      'pointermove',
      this.handleDocumentPointerMove
    );

    this.cartTriggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        this.handleCartTriggerClick
      );
    });

    this.mobileMenuButton?.addEventListener(
      'click',
      this.handleMobileMenuButtonClick
    );

    this.mobileMenuClose?.addEventListener(
      'click',
      this.handleMobileMenuCloseClick
    );

    this.mobileMenu?.addEventListener(
      'click',
      this.handleMobileMenuOverlayClick
    );

    this.mobileCarousels.forEach((carousel) => {
      carousel.addEventListener(
        'scroll',
        this.handleMobileCarouselScroll,
        {
          passive: true
        }
      );

      this.updateMobileCarouselScrollbar(
        carousel
      );
    });

    this.navItems.forEach((item) => {
      item.addEventListener(
        'mouseenter',
        this.handleNavItemMouseEnter
      );
    });

    this.megaMenuItems.forEach((item) => {
      item.addEventListener(
        'mouseenter',
        this.handleMegaMenuMouseEnter
      );

      item.addEventListener(
        'focusin',
        this.handleMegaMenuFocusIn
      );
    });

    this.megaMenuLinks.forEach((link) => {
      link.addEventListener(
        'mouseenter',
        this.handleMegaMenuLinkMouseEnter
      );

      link.addEventListener(
        'focus',
        this.handleMegaMenuLinkFocus
      );
    });
  }

  disconnectedCallback() {
    window.removeEventListener(
      'scroll',
      this.handleScroll
    );

    window.removeEventListener(
      'resize',
      this.handleResize
    );

    document.removeEventListener(
      'cart:update',
      this.handleCartUpdate
    );

    document.removeEventListener(
      'keydown',
      this.handleEscapeKey
    );

    document.removeEventListener(
      'pointermove',
      this.handleDocumentPointerMove
    );

    this.cartTriggers.forEach((trigger) => {
      trigger.removeEventListener(
        'click',
        this.handleCartTriggerClick
      );
    });

    this.mobileMenuButton?.removeEventListener(
      'click',
      this.handleMobileMenuButtonClick
    );

    this.mobileMenuClose?.removeEventListener(
      'click',
      this.handleMobileMenuCloseClick
    );

    this.mobileMenu?.removeEventListener(
      'click',
      this.handleMobileMenuOverlayClick
    );

    this.mobileCarousels.forEach((carousel) => {
      carousel.removeEventListener(
        'scroll',
        this.handleMobileCarouselScroll
      );
    });

    this.navItems.forEach((item) => {
      item.removeEventListener(
        'mouseenter',
        this.handleNavItemMouseEnter
      );
    });

    this.megaMenuItems.forEach((item) => {
      item.removeEventListener(
        'mouseenter',
        this.handleMegaMenuMouseEnter
      );

      item.removeEventListener(
        'focusin',
        this.handleMegaMenuFocusIn
      );
    });

    this.megaMenuLinks.forEach((link) => {
      link.removeEventListener(
        'mouseenter',
        this.handleMegaMenuLinkMouseEnter
      );

      link.removeEventListener(
        'focus',
        this.handleMegaMenuLinkFocus
      );
    });
  }

  handleScroll() {
    const shouldUseScrolledState =
      this.forceScrolled ||
      window.scrollY > this.scrollThreshold;

    this.classList.toggle(
      'is-scrolled',
      shouldUseScrolledState
    );

    this.dispatchHeaderState();
  }

  handleResize() {
    this.mobileCarousels.forEach((carousel) => {
      this.updateMobileCarouselScrollbar(
        carousel
      );
    });
  }

  handleCartUpdate(event) {
    const cart =
      event.detail &&
      event.detail.resource;

    if (
      cart &&
      typeof cart.item_count === 'number'
    ) {
      this.updateCartCount(
        cart.item_count
      );

      return;
    }

    this.refreshCartCount();
  }

  handleCartTriggerClick() {
    window.Rebuy?.SmartCart?.show();
  }

  handleNavItemMouseEnter(event) {
    const item =
      event.currentTarget;

    if (
      item.classList.contains(
        'site-header__nav-item--mega'
      )
    ) {
      return;
    }

    this.closeMegaMenus();
  }

  handleMegaMenuMouseEnter(event) {
    this.openMegaMenu(
      event.currentTarget
    );
  }

  handleMegaMenuFocusIn(event) {
    this.openMegaMenu(
      event.currentTarget
    );
  }

  handleMegaMenuLinkMouseEnter(event) {
    this.updateMegaMenuImage(
      event.currentTarget
    );
  }

  handleMegaMenuLinkFocus(event) {
    this.updateMegaMenuImage(
      event.currentTarget
    );
  }

  handleDocumentPointerMove(event) {
    if (!this.activeMegaMenuItem) {
      return;
    }

    const target =
      event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const activeMegaMenu =
      this.activeMegaMenuItem.querySelector(
        '.site-header__mega-menu'
      );

    const activeTrigger =
      this.activeMegaMenuItem.querySelector(
        '[data-mega-menu-trigger]'
      );

    const hoveredNavItem =
      target.closest(
        '.site-header__nav-item'
      );

    if (
      hoveredNavItem &&
      hoveredNavItem !==
        this.activeMegaMenuItem
    ) {
      this.closeMegaMenus();
      return;
    }

    if (
      activeTrigger?.contains(target)
    ) {
      return;
    }

    if (
      activeMegaMenu?.contains(target)
    ) {
      return;
    }

    if (!this.contains(target)) {
      this.closeMegaMenus();
    }
  }

  handleEscapeKey(event) {
    if (event.key !== 'Escape') {
      return;
    }

    this.closeMegaMenus();
    this.closeMobileMenu();
  }

  handleMobileMenuButtonClick() {
    if (
      this.classList.contains(
        'is-mobile-menu-open'
      )
    ) {
      this.closeMobileMenu();
      return;
    }

    this.openMobileMenu();
  }

  handleMobileMenuCloseClick() {
    this.closeMobileMenu();
  }

  handleMobileMenuOverlayClick(event) {
    if (
      event.target !==
      this.mobileMenu
    ) {
      return;
    }

    this.closeMobileMenu();
  }

  handleMobileCarouselScroll(event) {
    this.updateMobileCarouselScrollbar(
      event.currentTarget
    );
  }

  dispatchHeaderState() {
    this.dispatchEvent(
      new CustomEvent(
        'site-header:state-change',
        {
          bubbles: true,
          detail: {
            hasHeroHeader:
              this.classList.contains(
                'site-header--has-hero-header'
              ),

            isScrolled:
              this.classList.contains(
                'is-scrolled'
              ),

            isMegaMenuOpen:
              this.classList.contains(
                'is-mega-menu-open'
              ),

            isMobileMenuOpen:
              this.classList.contains(
                'is-mobile-menu-open'
              )
          }
        }
      )
    );
  }

  async refreshCartCount() {
    if (!this.cartCounts.length) {
      return;
    }

    try {
      const response =
        await fetch('/cart.js');

      if (!response.ok) {
        return;
      }

      const cart =
        await response.json();

      if (
        cart &&
        typeof cart.item_count ===
          'number'
      ) {
        this.updateCartCount(
          cart.item_count
        );
      }
    } catch (error) {
      // Silent fail. The count corrects itself
      // on the next page load.
    }
  }

  updateCartCount(count) {
    if (!this.cartCounts.length) {
      return;
    }

    this.cartCounts.forEach(
      (cartCount) => {
        const isMobileCount =
          cartCount.classList.contains(
            'site-header__cart-count--mobile'
          );

        cartCount.textContent =
          `(${count})`;

        cartCount.classList.toggle(
          'site-header__cart-count--hidden',
          !isMobileCount &&
            count === 0
        );

        cartCount.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    );
  }

  updateMobileCarouselScrollbar(
    carousel
  ) {
    if (!carousel) {
      return;
    }

    const section =
      carousel.closest(
        '.site-header__mobile-menu-section--carousel'
      );

    const thumb =
      section?.querySelector(
        '[data-mobile-carousel-thumb]'
      );

    if (!thumb) {
      return;
    }

    const scrollableWidth =
      carousel.scrollWidth -
      carousel.clientWidth;

    if (scrollableWidth <= 0) {
      thumb.style.setProperty(
        '--site-header-mobile-carousel-scrollbar-thumb-width',
        '100%'
      );

      thumb.style.setProperty(
        '--site-header-mobile-carousel-scrollbar-thumb-offset',
        '0px'
      );

      return;
    }

    const track =
      thumb.parentElement;

    if (!track) {
      return;
    }

    const trackWidth =
      track.clientWidth;

    const thumbWidth =
      Math.max(
        (
          carousel.clientWidth /
          carousel.scrollWidth
        ) * trackWidth,
        24
      );

    const maxThumbOffset =
      trackWidth -
      thumbWidth;

    const progress =
      carousel.scrollLeft /
      scrollableWidth;

    const thumbOffset =
      maxThumbOffset *
      progress;

    thumb.style.setProperty(
      '--site-header-mobile-carousel-scrollbar-thumb-width',
      `${thumbWidth}px`
    );

    thumb.style.setProperty(
      '--site-header-mobile-carousel-scrollbar-thumb-offset',
      `${thumbOffset}px`
    );
  }

  updateMegaMenuImage(link) {
    if (!link) {
      return;
    }

    const megaMenu =
      link.closest(
        '[data-mega-menu]'
      );

    if (!megaMenu) {
      return;
    }

    const image =
      megaMenu.querySelector(
        '[data-mega-menu-image]'
      );

    if (!image) {
      return;
    }

    const imageUrl =
      link.dataset
        .megaMenuImageUrl;

    if (!imageUrl) {
      return;
    }

    image.src =
      imageUrl;

    image.alt =
      link.dataset
        .megaMenuImageAlt || '';
  }

  openMegaMenu(item) {
    if (!item) {
      return;
    }

    const trigger =
      item.querySelector(
        '[data-mega-menu-trigger]'
      );

    this.closeMegaMenus(
      false
    );

    item.classList.add(
      'is-active'
    );

    trigger?.setAttribute(
      'aria-expanded',
      'true'
    );

    this.activeMegaMenuItem =
      item;

    this.classList.add(
      'is-mega-menu-open'
    );

    this.dispatchHeaderState();
  }

  closeMegaMenu(item) {
    if (!item) {
      return;
    }

    const trigger =
      item.querySelector(
        '[data-mega-menu-trigger]'
      );

    item.classList.remove(
      'is-active'
    );

    trigger?.setAttribute(
      'aria-expanded',
      'false'
    );
  }

  closeMegaMenus(
    shouldDispatch = true
  ) {
    this.megaMenuItems.forEach(
      (item) => {
        this.closeMegaMenu(
          item
        );
      }
    );

    this.activeMegaMenuItem =
      null;

    this.classList.remove(
      'is-mega-menu-open'
    );

    if (shouldDispatch) {
      this.dispatchHeaderState();
    }
  }

  openMobileMenu() {
    this.closeMegaMenus(
      false
    );

    this.classList.add(
      'is-mobile-menu-open'
    );

    this.mobileMenuButton
      ?.setAttribute(
        'aria-expanded',
        'true'
      );

    this.mobileCarousels.forEach(
      (carousel) => {
        this.updateMobileCarouselScrollbar(
          carousel
        );
      }
    );

    this.dispatchHeaderState();
  }

  closeMobileMenu() {
    this.classList.remove(
      'is-mobile-menu-open'
    );

    this.mobileMenuButton
      ?.setAttribute(
        'aria-expanded',
        'false'
      );

    this.dispatchHeaderState();
  }
}

if (
  !customElements.get(
    'site-header'
  )
) {
  customElements.define(
    'site-header',
    SiteHeader
  );
}